/**
 * Facility Benchmarks Component
 * 
 * Displays facility performance compared to anonymized industry benchmarks.
 * Allows facilities to see how they compare without revealing other facilities' identities.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Chip,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Skeleton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Leaderboard as LeaderboardIcon,
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import {
  getFacilityBenchmarks,
  getBenchmarkCategories,
  getPerformanceTierColor,
  getVarianceColor,
  formatBenchmarkValue,
  isHigherBetter,
} from '../../services/benchmarkService';

// Styled Components
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)',
}));

const BenchmarkCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
  overflow: 'visible',
}));

const MetricCard = styled(Card)(({ theme, tier }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `2px solid ${getPerformanceTierColor(tier)}20`,
  borderLeft: `4px solid ${getPerformanceTierColor(tier)}`,
  transition: 'all 0.2s ease-in-out',
  height: '100%',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const PercentileBar = styled(Box)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[200],
  position: 'relative',
  marginTop: theme.spacing(1),
}));

const PercentileFill = styled(Box)(({ width, color }) => ({
  height: '100%',
  borderRadius: 4,
  backgroundColor: color,
  width: `${width}%`,
  transition: 'width 0.5s ease-in-out',
}));

const PercentileMarker = styled(Box)(({ position }) => ({
  position: 'absolute',
  top: -4,
  left: `${position}%`,
  transform: 'translateX(-50%)',
  width: 16,
  height: 16,
  borderRadius: '50%',
  backgroundColor: '#1976d2',
  border: '2px solid white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
}));

const categoryLabels = {
  patient_volume: 'Patient Volume',
  clinical_operations: 'Clinical Operations',
  scheduling: 'Scheduling',
  revenue_cycle: 'Revenue Cycle',
  quality: 'Quality',
};

export default function FacilityBenchmarks() {
  const userData = useUserData();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const facilityId = userData.defaultFacility?.id;

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const result = await getBenchmarkCategories();
        setCategories(result.categories || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Fetch benchmark data
  const fetchBenchmarks = useCallback(async () => {
    if (!facilityId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const data = await getFacilityBenchmarks(facilityId, selectedCategory);
      setBenchmarkData(data);
    } catch (err) {
      console.error('Error fetching benchmarks:', err);
      setError(err.message || 'Failed to load benchmark data');
    } finally {
      setLoading(false);
    }
  }, [facilityId, selectedCategory]);

  useEffect(() => {
    fetchBenchmarks();
  }, [fetchBenchmarks]);

  // Summary metrics
  const summaryMetrics = useMemo(() => {
    if (!benchmarkData?.summary) return null;
    return benchmarkData.summary;
  }, [benchmarkData]);

  // Render performance tier chip
  const renderTierChip = (tier) => {
    return (
      <Chip
        label={tier}
        size="small"
        sx={{
          backgroundColor: `${getPerformanceTierColor(tier)}20`,
          color: getPerformanceTierColor(tier),
          fontWeight: 600,
          fontSize: '0.75rem',
        }}
      />
    );
  };

  // Render variance indicator
  const renderVarianceIndicator = (variance, metricId) => {
    if (variance === null || variance === undefined) return null;
    
    const higherBetter = isHigherBetter(metricId);
    const isPositive = higherBetter ? variance > 0 : variance < 0;
    const color = getVarianceColor(variance, higherBetter);
    
    const Icon = variance > 0 ? TrendingUpIcon : variance < 0 ? TrendingDownIcon : TrendingFlatIcon;
    
    return (
      <Tooltip title="vs Industry Average" arrow>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
          <Icon sx={{ fontSize: 16, color }} />
          <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
            {variance > 0 ? '+' : ''}{variance.toFixed(1)}%
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  // Render percentile bar with marker
  const renderPercentileBar = (percentile, tier) => {
    const color = getPerformanceTierColor(tier);
    
    return (
      <Tooltip title={`${percentile?.toFixed(0) || 0}th percentile`}>
        <PercentileBar>
          <PercentileFill width={percentile || 0} color={`${color}40`} />
          <PercentileMarker position={percentile || 0} />
        </PercentileBar>
      </Tooltip>
    );
  };

  // Render metric card
  const renderMetricCard = (metric) => {
    return (
      <Grid item xs={12} sm={6} md={4} key={metric.metricId}>
        <MetricCard tier={metric.performanceTier}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {metric.metricName}
              </Typography>
              {renderTierChip(metric.performanceTier)}
            </Box>
            
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {formatBenchmarkValue(metric.facilityValue, metric.metricId)}
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Industry Avg: {formatBenchmarkValue(metric.industryAvg, metric.metricId)}
              </Typography>
              {renderVarianceIndicator(metric.variancePercent, metric.metricId)}
            </Box>
            
            {renderPercentileBar(metric.percentileRank, metric.performanceTier)}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                25th: {formatBenchmarkValue(metric.industryP25, metric.metricId)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Median: {formatBenchmarkValue(metric.industryMedian, metric.metricId)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                75th: {formatBenchmarkValue(metric.industryP75, metric.metricId)}
              </Typography>
            </Box>
          </CardContent>
        </MetricCard>
      </Grid>
    );
  };

  if (!facilityId) {
    return (
      <Alert severity="warning">
        No facility selected. Please select a facility to view benchmarks.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      {/* Page Header */}
      <HeaderCard>
        <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LeaderboardIcon sx={{ fontSize: 32, color: 'white' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                Industry Benchmarks
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Compare your facility's performance against anonymized industry averages
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      {/* Filters and Summary */}
      <BenchmarkCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Performance Overview
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Category"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton onClick={fetchBenchmarks} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Summary Stats */}
          {summaryMetrics && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, py: 0.5, px: 1.5, bgcolor: 'grey.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1 }}>
                  {summaryMetrics.totalMetrics}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Metrics
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, py: 0.5, px: 1.5, bgcolor: 'success.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'success.main' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', lineHeight: 1 }}>
                  {summaryMetrics.metricsAboveAvg}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Above Average
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, py: 0.5, px: 1.5, bgcolor: 'success.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'success.dark' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.dark', lineHeight: 1 }}>
                  {summaryMetrics.topPerformerCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Top Performer
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, py: 0.5, px: 1.5, bgcolor: 'error.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'error.main' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main', lineHeight: 1 }}>
                  {summaryMetrics.needsImprovementCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Needs Improvement
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </BenchmarkCard>

      {/* Loading State */}
      {loading && (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Benchmark Cards by Category */}
      {!loading && benchmarkData?.benchmarks && (
        <>
          {Object.entries(benchmarkData.benchmarks).map(([category, metrics]) => (
            <Box key={category} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                {categoryLabels[category] || category}
                <Tooltip title="Benchmarks are calculated from anonymized data across all facilities">
                  <InfoIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </Tooltip>
              </Typography>
              <Grid container spacing={3}>
                {metrics.map(renderMetricCard)}
              </Grid>
            </Box>
          ))}
        </>
      )}

      {/* No Data State */}
      {!loading && !error && (!benchmarkData?.benchmarks || Object.keys(benchmarkData.benchmarks).length === 0) && (
        <Alert severity="info">
          No benchmark data available. Benchmarks are calculated daily from facility performance data.
        </Alert>
      )}

      {/* Disclaimer */}
      <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Note:</strong> Industry benchmarks are calculated from anonymized aggregate data. 
          No individual facility's identity or specific data is revealed. Benchmarks are updated daily 
          and based on the performance of {benchmarkData?.benchmarks?.[Object.keys(benchmarkData?.benchmarks || {})[0]]?.[0]?.facilityCount || 'multiple'} facilities.
        </Typography>
      </Box>
    </Box>
  );
}

